import { FilterField, labelId } from "kontrolEdge/helpers/advancedFilters";
import { keListTagFilterHaystack } from "kontrolEdge/components/Shared/mapKeListResourceTags";
import { DataCenterConnection } from "./DatacenterData";

// Reuse the same parsers you used for Internet
function parseMbpsInput(raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(/\s+/g, "").replace(/,/g, "");
  if (/(gbps?|g)b?$/.test(s)) {
    const n = Number(s.replace(/(gbps?|g)b?$/, ""));
    return Number.isFinite(n) ? n * 1000 : null;
  }
  if (/(mbps?|m)b?$/.test(s)) {
    const n = Number(s.replace(/(mbps?|m)b?$/, ""));
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// Accept D/M/YYYY, DD/MM/YYYY, YYYY-MM-DD, etc.
function parseDateInput(raw: string): Date | null {
  const s = raw.trim();
  const m1 = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m1) {
    const [, dd, mm, yyyy] = m1;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(d.getTime()) ? null : d;
  }
  const m2 = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (m2) {
    const [, yyyy, mm, dd] = m2;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function makeDatacenterFields(
  isViewingAllCustomers: boolean
): FilterField<DataCenterConnection>[] {
  const fields: FilterField<DataCenterConnection>[] = [
    {
      label: "DCI ID",
      key: "dciid",
      accessor: (r) => r.subscriptionId,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Parent Connection",
      key: "parentconnection",
      accessor: (r) =>
        [r.parentConnection?.labelA, r.parentConnection?.labelB]
          .filter(Boolean)
          .join(", "),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Name",
      key: "name",
      accessor: (r) => r.name,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Mbps",
      key: "mbps",
      accessor: (r) => r.mbpsNum,
      type: "number",
      inFreeText: true,
      parseRhs: parseMbpsInput,
    },
    {
      label: "VLAN",
      key: "vlan",
      accessor: (r) => r.vlan,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Term",
      key: "term",
      accessor: (r) => r.terms,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Renew Date",
      key: "renewdate",
      accessor: (r) => r.renewDateRaw,
      type: "date",
      inFreeText: true,
      parseRhs: parseDateInput,
    },
    {
      label: "Tag",
      key: "tag",
      accessor: (r) => keListTagFilterHaystack(r),
      type: "string",
      inFreeText: true,
    },
  ];

  if (isViewingAllCustomers) {
    fields.splice(1, 0, {
      label: "Customer",
      key: "customer",
      accessor: (r) => [
        r.customer?.name ?? "",
        r.customer?.chargebeeCustomerId ?? ""
      ].filter(Boolean).join(" "),
      type: "string",
      inFreeText: true,
    });
  }

  // Hidden catch-all to make free-text super forgiving
  fields.push({
    label: "SearchText",
    key: "__search",
    accessor: (r) =>
      [
        r.subscriptionId,
        r.name,
        r.vlan,
        r.terms,
        r.parentConnection?.labelA,
        r.parentConnection?.labelB,
        keListTagFilterHaystack(r),
      ]
        .filter(Boolean)
        .join(" "),
    type: "string",
    inFreeText: true,
  });

  for (const f of fields) f.key ||= labelId(f.label);
  return fields;
}
